import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Typography } from '@mui/material';
import useAiChatPanel from '../../hooks/useAiChatPanel';
import useWindowSize from '../../hooks/useWindowSize';
import { LoadingButton } from '../Button';
import IconButton from '../IconButton';
import { ResizablePanelHorizontal } from '../ResizablePanel';
import {
  MAX_PANEL_WIDTH,
  MEDIUM_PANEL_WIDTH,
  SMALL_PANEL_WIDTH,
  SMALL_PANEL_RELATIVE_WIDTH,
  MEDIUM_PANEL_RELATIVE_WIDTH,
} from './constants';
import {
  DrawerStyled,
  Wrapper,
  Header,
  Footer,
  Content,
  CloseIconStyled,
} from './shared';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  title?: string | React.ReactNode;
  children: React.ReactNode;
  dragHandleOffset?: { x: number; y: number };
  isLoading?: boolean;
  submitButtonLabel?: string;
  onSubmit?: () => void;
  submitDisabled?: boolean;
  noPadding?: boolean;
  className?: string;
  width?: 'small' | 'medium';
};

const Drawer = ({
  isOpen,
  onClose,
  title,
  children,
  dragHandleOffset,
  isLoading,
  submitButtonLabel,
  onSubmit,
  submitDisabled,
  noPadding,
  className,
  width = 'medium',
}: Props) => {
  const [panelWidth, setPanelWidth] = useState(0);
  const { width: windowWidth } = useWindowSize();
  const { isExpanded: isAiChatPanelOpen, width: aiChatPanelWidth } =
    useAiChatPanel();
  const isAiChatPanelVisible = isAiChatPanelOpen;
  const rightOffset = isAiChatPanelVisible ? aiChatPanelWidth : 0;

  const minWidth = width === 'small' ? SMALL_PANEL_WIDTH : MEDIUM_PANEL_WIDTH;
  const relativeWidth =
    width === 'small'
      ? SMALL_PANEL_RELATIVE_WIDTH
      : MEDIUM_PANEL_RELATIVE_WIDTH;

  useEffect(() => {
    if (isOpen && panelWidth) {
      return;
    }
    const resolvedRelativeWidth = Math.round(windowWidth * relativeWidth);
    const validWidth = Math.max(
      Math.min(resolvedRelativeWidth, MAX_PANEL_WIDTH),
      minWidth,
    );
    setPanelWidth(validWidth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowWidth, isOpen]);

  return (
    <>
      {isOpen &&
        createPortal(
          <ResizablePanelHorizontal
            width={panelWidth}
            minWidth={minWidth}
            maxWidth={MAX_PANEL_WIDTH}
            onDrag={setPanelWidth}
            onMouseEnter={() => null}
            onMouseLeave={() => null}
            direction="right-to-left"
            positionOffset={dragHandleOffset}
            rightOffset={rightOffset}
          />,
          document.body,
        )}

      <DrawerStyled
        className={className}
        open={isOpen}
        onClose={onClose}
        $rightOffset={rightOffset}
      >
        <Wrapper
          className="drawer-wrapper"
          $width={panelWidth}
          $rightOffset={rightOffset}
        >
          <Header>
            {typeof title === 'string' ? (
              <Typography sx={{ fontSize: '16px', fontWeight: 'bold' }}>
                {title}
              </Typography>
            ) : (
              title
            )}
            <IconButton onClick={onClose}>
              <CloseIconStyled />
            </IconButton>
          </Header>

          <Content $noPadding={noPadding}>{children}</Content>

          {!!onSubmit && (
            <Footer>
              <LoadingButton
                type="submit"
                size="large"
                disabled={submitDisabled}
                loading={isLoading}
                onClick={onSubmit}
              >
                {submitButtonLabel}
              </LoadingButton>
            </Footer>
          )}
        </Wrapper>
      </DrawerStyled>
    </>
  );
};

export default Drawer;
